import { closeOpenContainersRule } from './rules/closeOpenContainers';
import { closeUnterminatedStringRule } from './rules/closeUnterminatedString';
import { dropExtraClosersRule } from './rules/dropExtraClosers';
import { dropTrailingContentRule } from './rules/dropTrailingContent';
import { escapeControlCharacterRule } from './rules/escapeControlCharacter';
import { fixNumberLiteralRule } from './rules/fixNumberLiteral';
import { insertMissingColonRule } from './rules/insertMissingColon';
import { insertMissingCommaRule } from './rules/insertMissingComma';
import { normalizeJsLiteralRule } from './rules/normalizeJsLiteral';
import { normalizeNonFiniteRule } from './rules/normalizeNonFinite';
import { normalizePythonLiteralRule } from './rules/normalizePythonLiteral';
import { normalizeSmartQuotesRule } from './rules/normalizeSmartQuotes';
import { quoteUnquotedKeyRule } from './rules/quoteUnquotedKey';
import { removeCommentRule } from './rules/removeComment';
import { removeStrayCommaRule } from './rules/removeStrayComma';
import { removeTrailingCommaRule } from './rules/removeTrailingComma';
import { requoteSingleQuotedRule } from './rules/requoteSingleQuoted';
import { restoreMissingQuoteRule } from './rules/restoreMissingQuote';
import { stripBomRule } from './rules/stripBom';
import type { RepairRule } from './types';

/**
 * Every repair rule of v1. **This is the only file that enumerates them** (ADR-0011): adding
 * a rule is a new file under `rules/`, its test, one import and one line here — plus the
 * message template, which the `MessageCatalog` type demands at compile time.
 *
 * The array is listed in `priority` order, which is the order the driver resolves edit
 * conflicts in, not the order rules run in: every rule sees the same frozen context and none
 * can observe another's output within a pass. That independence is the property the legacy
 * pipeline lacked — there, twenty rewrites shared one mutable string and each one changed
 * what the next could see (recon §2.1).
 *
 * The priorities themselves encode one real decision each. Removing text comes before
 * rewriting it (a comment is gone before anything inspects what it contained); rewriting
 * comes before inserting; and `close-unterminated-string` comes before
 * `close-open-containers` so that `{"a":"x` gains its quote before its brace, since both
 * insert at the very same offset and the order decides whether the result is `{"a":"x"}` or
 * `{"a":"x}"`.
 */
export const ALL_REPAIR_RULES: readonly RepairRule[] = [
  stripBomRule,
  removeCommentRule,
  removeTrailingCommaRule,
  removeStrayCommaRule,
  restoreMissingQuoteRule,
  insertMissingCommaRule,
  insertMissingColonRule,
  quoteUnquotedKeyRule,
  requoteSingleQuotedRule,
  normalizeSmartQuotesRule,
  escapeControlCharacterRule,
  normalizeJsLiteralRule,
  normalizePythonLiteralRule,
  normalizeNonFiniteRule,
  fixNumberLiteralRule,
  closeUnterminatedStringRule,
  closeOpenContainersRule,
  dropExtraClosersRule,
  dropTrailingContentRule,
];
